import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styles from './HomeOwner.module.css';
import guestSessionService from '../../services/guestSessionService';
import { proposalComparisonService } from '../../services/api';
import GuestMigrationPopup from '../../components/common/GuestMigrationPopup/GuestMigrationPopup';

const HomeOwner = () => {
  const navigate = useNavigate();
  const [guestSessions, setGuestSessions] = useState([]);
  const [userProjects, setUserProjects] = useState([]);
  const [showGuestSessions, setShowGuestSessions] = useState(false);
  const [showUserProjects, setShowUserProjects] = useState(false);
  const [showMigrationPopup, setShowMigrationPopup] = useState(false);
  const [migrationData, setMigrationData] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  
  // Pagination states
  const [userProjectsPage, setUserProjectsPage] = useState(1);
  const [guestSessionsPage, setGuestSessionsPage] = useState(1);
  const itemsPerPage = 6;

  // Testimonials carousel ref and state
  const testimonialGridRef = useRef(null);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const testimonialCount = 3;

  // How It Works step state
  const [activeStep, setActiveStep] = useState(1);
  const [isAutoMode, setIsAutoMode] = useState(true);
  const [renderKey, setRenderKey] = useState(0);

  // Check authentication status
  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    setIsAuthenticated(!!token);
    setUser(userData);
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      // User is logged in - load their projects and check for pending migration
      loadUserProjects();
      checkForPendingMigration();
    } else {
      // User is not logged in - show guest sessions
      loadGuestSessions();
    }
  }, [isAuthenticated]);

  // Auto-scroll testimonials
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => {
        const nextIndex = (prev + 1) % testimonialCount;
        
        // Scroll to the next testimonial on mobile
        if (testimonialGridRef.current && window.innerWidth <= 768) {
          const cardWidth = testimonialGridRef.current.children[0]?.offsetWidth || 0;
          const gap = 16; // 1rem gap
          const scrollPosition = nextIndex * (cardWidth + gap);
          
          testimonialGridRef.current.scrollTo({
            left: scrollPosition,
            behavior: 'smooth'
          });
        }
        
        return nextIndex;
      });
    }, 4000); // Change every 4 seconds

    return () => clearInterval(interval);
  }, [testimonialCount]);

  // Auto-advance steps on mobile
  useEffect(() => {
    let interval;
    if (window.innerWidth <= 768 && isAutoMode) {
      interval = setInterval(() => {
        setActiveStep((prev) => (prev % 3) + 1);
      }, 5000);
    }
    return () => interval && clearInterval(interval);
  }, [isAutoMode]);

  // Reset to auto after 15 seconds of manual interaction
  useEffect(() => {
    let timeout;
    if (!isAutoMode) {
      timeout = setTimeout(() => {
        setRenderKey(prev => prev + 1);
        setActiveStep(1);
        setIsAutoMode(true);
      }, 15000);
    }
    return () => timeout && clearTimeout(timeout);
  }, [isAutoMode]);

  const handleStepClick = (stepNumber) => {
    setActiveStep(stepNumber);
    setIsAutoMode(false);
  };

  const loadGuestSessions = () => {
    // Check for existing guest sessions
    const sessions = guestSessionService.getRecentSessions();
    setGuestSessions(sessions);
    setShowGuestSessions(sessions.length > 0);
    
    // Clean up old sessions on load
    guestSessionService.cleanupOldSessions();
  };

  const loadUserProjects = async () => {
    try {
      const response = await proposalComparisonService.getUserComparisons();
      if (response.data && response.data.status === 'success') {
        setUserProjects(response.data.data || []);
        setShowUserProjects(response.data.data?.length > 0);
      }
    } catch (error) {
      console.error('Error loading user projects:', error);
      // If there's an auth error, user might need to log in again
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setIsAuthenticated(false);
        setUser(null);
      }
    }
  };

  const checkForPendingMigration = () => {
    const pendingMigration = localStorage.getItem('pendingGuestMigration');
    if (pendingMigration) {
      try {
        const migrationInfo = JSON.parse(pendingMigration);
        if (migrationInfo.showImportPopup && migrationInfo.guestSessions?.length > 0) {
          setMigrationData(migrationInfo);
          setShowMigrationPopup(true);
          // Remove the pending migration data
          localStorage.removeItem('pendingGuestMigration');
        }
      } catch (error) {
        console.error('Error parsing pending migration data:', error);
        localStorage.removeItem('pendingGuestMigration');
      }
    }
  };

  const handleViewGuestSession = (session) => {
    navigate(`/homeowner/comparisons/${session.comparisonId}?guest=true`);
  };

  const handleViewUserProject = (project) => {
    navigate(`/homeowner/comparisons/${project.id}`);
  };

  const handleDeleteGuestSession = (sessionId) => {
    guestSessionService.deleteGuestSession(sessionId);
    const updatedSessions = guestSessionService.getRecentSessions();
    setGuestSessions(updatedSessions);
    setShowGuestSessions(updatedSessions.length > 0);
  };

  const handleMigrationComplete = (action, count) => {
    setShowMigrationPopup(false);
    setMigrationData(null);
    
    if (action === 'imported') {
      // Refresh user projects to show the newly imported ones
      loadUserProjects();
      // Show success message
      alert(`Successfully imported ${count} project${count !== 1 ? 's' : ''} to your account!`);
    } else if (action === 'deleted') {
      // Show deletion confirmation
      alert(`Successfully deleted ${count} guest project${count !== 1 ? 's' : ''}!`);
    }
  };

  const handleCloseMigrationPopup = () => {
    setShowMigrationPopup(false);
    setMigrationData(null);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) return 'Today';
    if (diffDays === 2) return 'Yesterday';
    if (diffDays <= 7) return `${diffDays - 1} days ago`;
    return date.toLocaleDateString();
  };

  const formatUserProjectDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  // Pagination helper functions
  const getPaginatedItems = (items, page, perPage) => {
    const startIndex = (page - 1) * perPage;
    const endIndex = startIndex + perPage;
    return items.slice(startIndex, endIndex);
  };

  const getTotalPages = (items, perPage) => {
    return Math.ceil(items.length / perPage);
  };

  const handlePageChange = (type, newPage) => {
    if (type === 'userProjects') {
      setUserProjectsPage(newPage);
    } else if (type === 'guestSessions') {
      setGuestSessionsPage(newPage);
    }
  };

  // Get paginated data
  const paginatedUserProjects = getPaginatedItems(userProjects, userProjectsPage, itemsPerPage);
  const paginatedGuestSessions = getPaginatedItems(guestSessions, guestSessionsPage, itemsPerPage);
  const userProjectsTotalPages = getTotalPages(userProjects, itemsPerPage);
  const guestSessionsTotalPages = getTotalPages(guestSessions, itemsPerPage);

  return (
    <div className={styles.home}>
      {/* Migration Popup */}
      {showMigrationPopup && migrationData && (
        <GuestMigrationPopup
          guestSessions={migrationData.guestSessions}
          userId={migrationData.userId}
          onClose={handleCloseMigrationPopup}
          onComplete={handleMigrationComplete}
        />
      )}

      {/* Hero Section */}
      <section className={styles.hero}>
        <div className={styles.heroBackground}>
          <div className={styles.overlay}></div>
          <div className={styles.shapes}>
            <div className={styles.shape}></div>
            <div className={styles.shape}></div>
            <div className={styles.shape}></div>
          </div>
        </div>
        <div className={styles.heroContent}>
          <h1>
            Compare Contractor Bids. Instantly.
          </h1>
          <p>
            Stop guessing. Upload your quotes—let AI show you what's missing, overpriced, or unclear.
          </p>
          <div className={styles.heroButtons}>
            <Link to="/homeowner/create-comparison" className={styles.buttonPrimary}>
              🚀 Start FREE Comparison Now
              <i className="fas fa-arrow-right"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Value Propositions Section */}
      <section className={styles.valuePropositions}>
        <div className={styles.container}>
          <div className={styles.sectionTitle}>
            <h2>Why Smart Homeowners Choose Trakvu</h2>
            <p>Join thousands who stopped second-guessing and started making confident contractor decisions</p>
          </div>
          <div className={styles.featuresGrid}>
            <div className={styles.featureCard}>
              <div className={styles.featureIconWrapper}>
                <div className={styles.featureIcon}>✅</div>
                <div className={styles.featureBadge}>EASY</div>
              </div>
              <h3>No Tech Skills Needed</h3>
              <p>Simply upload your contractor quotes and let our AI do the heavy lifting. No spreadsheets, no confusion.</p>
              <div className={styles.featureFooter}>
                <span className={styles.featureTime}>⏱️ 2 minutes to upload</span>
              </div>
            </div>
            <div className={styles.featureCard}>
              <div className={styles.featureIconWrapper}>
                <div className={styles.featureIcon}>🧠</div>
                <div className={styles.featureBadge}>SMART</div>
              </div>
              <h3>Smarter Than Side-by-Side</h3>
              <p>Our AI catches missing items, inflated prices, and red flags that even experienced homeowners miss.</p>
              <div className={styles.featureFooter}>
                <span className={styles.featureTime}>🎯 95% accuracy rate</span>
              </div>
            </div>
            <div className={styles.featureCard}>
              <div className={styles.featureIconWrapper}>
                <div className={styles.featureIcon}>🔒</div>
                <div className={styles.featureBadge}>SECURE</div>
              </div>
              <h3>Private and Secure</h3>
              <p>Your contractor quotes and personal information are encrypted and never shared with anyone. Period.</p>
              <div className={styles.featureFooter}>
                <span className={styles.featureTime}>🔐 Bank-level encryption</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className={styles.testimonialsSection}>
        <div className={styles.container}>
          <div className={styles.sectionTitle}>
            <h2>What Homeowners Are Saying</h2>
            <p>Real stories from homeowners who used our AI to find the best contractor</p>
          </div>
          <div className={styles.testimonialGrid} ref={testimonialGridRef}>
            <div className={styles.testimonialCard}>
              <div className={styles.testimonialQuote}>
                "Trakvu showed me that one contractor left out demolition—saved me $9,300."
              </div>
              <div className={styles.testimonialAuthor}>
                <div className={styles.testimonialAvatar}></div>
                <div>
                  <h4>Sarah M.</h4>
                  <p>Charlotte, NC</p>
                  <div className={styles.stars}>⭐⭐⭐⭐⭐</div>
                </div>
              </div>
            </div>
            <div className={styles.testimonialCard}>
              <div className={styles.testimonialQuote}>
                "Found a $5,000 price difference and quality issues I would have missed completely."
              </div>
              <div className={styles.testimonialAuthor}>
                <div className={styles.testimonialAvatar}></div>
                <div>
                  <h4>Mike R.</h4>
                  <p>Austin, TX</p>
                  <div className={styles.stars}>⭐⭐⭐⭐⭐</div>
                </div>
              </div>
            </div>
            <div className={styles.testimonialCard}>
              <div className={styles.testimonialQuote}>
                "The AI caught red flags in permits and timeline that saved my kitchen remodel."
              </div>
              <div className={styles.testimonialAuthor}>
                <div className={styles.testimonialAvatar}></div>
                <div>
                  <h4>Lisa K.</h4>
                  <p>Denver, CO</p>
                  <div className={styles.stars}>⭐⭐⭐⭐⭐</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* User Projects Section (for authenticated users) */}
      {isAuthenticated && showUserProjects && (
        <section className={styles.guestSessionsSection}>
          <div className={styles.sectionContainer}>
            <div className={styles.projectsContainer}>
              <div className={styles.guestSessionsHeader}>
                <div className={styles.headerContent}>
                  <div className={styles.headerIcon}>
                    <i className="fas fa-folder-open"></i>
                  </div>
                  <div>
                    <h3>Your Projects</h3>
                    <p>You have {userProjects.length} project{userProjects.length > 1 ? 's' : ''} in your account</p>
                  </div>
                </div>
              </div>
              
              <div className={userProjectsTotalPages > 1 ? styles.guestSessionsGridPaginated : styles.guestSessionsGrid}>
                {paginatedUserProjects.map((project) => (
                  <div key={project.id} className={styles.sessionCard}>
                    <div className={styles.sessionHeader}>
                      <div className={styles.sessionIcon}>
                        <i className="fas fa-file-contract"></i>
                      </div>
                      <div className={styles.sessionMeta}>
                        <h4>{project.project_name}</h4>
                        <p className={styles.projectType}>{project.project_type || 'Home Improvement'}</p>
                        <p className={styles.sessionDate}>Created {formatUserProjectDate(project.created_at)}</p>
                      </div>
                    </div>
                    
                    <div className={styles.sessionContent}>
                      <div className={styles.sessionStats}>
                        <div className={styles.stat}>
                          <i className="fas fa-file-contract"></i>
                          <span>Proposals</span>
                          <strong>{(project.contractors || []).length}</strong>
                        </div>
                        {project.project_budget && (
                          <div className={styles.stat}>
                            <i className="fas fa-dollar-sign"></i>
                            <span>Budget</span>
                            <strong>${project.project_budget}</strong>
                          </div>
                        )}
                      </div>
                      
                      <div className={styles.sessionActions}>
                        <button 
                          onClick={() => handleViewUserProject(project)}
                          className={styles.viewButton}
                        >
                          <i className="fas fa-eye"></i>
                          View Report
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* User Projects Pagination */}
              {userProjectsTotalPages > 1 && (
                <div className={styles.pagination}>
                  <button 
                    onClick={() => handlePageChange('userProjects', userProjectsPage - 1)}
                    disabled={userProjectsPage === 1}
                    className={styles.paginationButton}
                  >
                    <i className="fas fa-chevron-left"></i>
                    Previous
                  </button>
                  
                  <div className={styles.paginationInfo}>
                    <span className={styles.pageNumbers}>
                      {Array.from({ length: userProjectsTotalPages }, (_, i) => i + 1).map(page => (
                        <button
                          key={page}
                          onClick={() => handlePageChange('userProjects', page)}
                          className={`${styles.pageNumber} ${page === userProjectsPage ? styles.active : ''}`}
                        >
                          {page}
                        </button>
                      ))}
                    </span>
                    <span className={styles.pageInfo}>
                      Page {userProjectsPage} of {userProjectsTotalPages} • {userProjects.length} total projects
                    </span>
                  </div>
                  
                  <button 
                    onClick={() => handlePageChange('userProjects', userProjectsPage + 1)}
                    disabled={userProjectsPage === userProjectsTotalPages}
                    className={styles.paginationButton}
                  >
                    Next
                    <i className="fas fa-chevron-right"></i>
                  </button>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Guest Sessions Section (for non-authenticated users) */}
      {!isAuthenticated && showGuestSessions && (
        <section className={styles.guestSessionsSection}>
          <div className={styles.sectionContainer}>
            <div className={styles.projectsContainer}>
              <div className={styles.guestSessionsHeader}>
                <div className={styles.headerContent}>
                  <div className={styles.headerIcon}>
                    <i className="fas fa-history"></i>
                  </div>
                  <div>
                    <h3>Your Previous Projects</h3>
                    <p>You have {guestSessions.length} project{guestSessions.length > 1 ? 's' : ''} from your recent visits</p>
                  </div>
                </div>
              </div>
              
              <div className={guestSessionsTotalPages > 1 ? styles.guestSessionsGridPaginated : styles.guestSessionsGrid}>
                {paginatedGuestSessions.map((session) => (
                  <div key={session.id} className={styles.sessionCard}>
                    <div className={styles.sessionHeader}>
                      <div className={styles.sessionIcon}>
                        <i className="fas fa-file-contract"></i>
                      </div>
                      <div className={styles.sessionMeta}>
                        <h4>{session.projectName}</h4>
                        <p className={styles.projectType}>{session.projectType}</p>
                        <p className={styles.sessionDate}>Created {formatDate(session.createdAt)}</p>
                      </div>
                    </div>
                    
                    <div className={styles.sessionContent}>
                      <div className={styles.sessionStats}>
                        <div className={styles.stat}>
                          <i className="fas fa-file-contract"></i>
                          <span>Proposals</span>
                          <strong>{session.contractorCount || (session.data?.contractors || []).length || 0}</strong>
                        </div>
                        {session.metadata.budget && (
                          <div className={styles.stat}>
                            <i className="fas fa-dollar-sign"></i>
                            <span>Budget</span>
                            <strong>${session.metadata.budget}</strong>
                          </div>
                        )}
                      </div>
                      
                      <div className={styles.sessionActions}>
                        <button 
                          onClick={() => handleViewGuestSession(session)}
                          className={styles.viewButton}
                        >
                          <i className="fas fa-eye"></i>
                          View Report
                        </button>
                        <button 
                          onClick={() => handleDeleteGuestSession(session.id)}
                          className={styles.deleteButton}
                          title="Delete this project"
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Guest Sessions Pagination */}
              {guestSessionsTotalPages > 1 && (
                <div className={styles.pagination}>
                  <button 
                    onClick={() => handlePageChange('guestSessions', guestSessionsPage - 1)}
                    disabled={guestSessionsPage === 1}
                    className={styles.paginationButton}
                  >
                    <i className="fas fa-chevron-left"></i>
                    Previous
                  </button>
                  
                  <div className={styles.paginationInfo}>
                    <span className={styles.pageNumbers}>
                      {Array.from({ length: guestSessionsTotalPages }, (_, i) => i + 1).map(page => (
                        <button
                          key={page}
                          onClick={() => handlePageChange('guestSessions', page)}
                          className={`${styles.pageNumber} ${page === guestSessionsPage ? styles.active : ''}`}
                        >
                          {page}
                        </button>
                      ))}
                    </span>
                    <span className={styles.pageInfo}>
                      Page {guestSessionsPage} of {guestSessionsTotalPages} • {guestSessions.length} total projects
                    </span>
                  </div>
                  
                  <button 
                    onClick={() => handlePageChange('guestSessions', guestSessionsPage + 1)}
                    disabled={guestSessionsPage === guestSessionsTotalPages}
                    className={styles.paginationButton}
                  >
                    Next
                    <i className="fas fa-chevron-right"></i>
                  </button>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Benefits Section */}
      {/* <section className={styles.benefitsSection}>
        <div className={styles.sectionTitle}>
          <h2>Why Choose SmartTech ProServe for Your Renovation?</h2>
          <p>
            Our AI-powered platform helps you make informed decisions for your
            home improvement projects
          </p>
        </div>
        <div className={styles.benefitsGrid}>
          <div className={styles.benefitCard}>
            <div className={styles.iconWrapper}>
              <i className="fas fa-robot"></i>
            </div>
            <h3>AI-Powered Analysis</h3>
            <p>
              Our advanced AI evaluates contractor proposals based on quality,
              timeline, materials, and value
            </p>
          </div>
          <div className={styles.benefitCard}>
            <div className={styles.iconWrapper}>
              <i className="fas fa-chart-line"></i>
            </div>
            <h3>Proposal Comparison</h3>
            <p>
              Easily compare multiple contractor bids side-by-side with our
              intelligent rating system
            </p>
          </div>
          <div className={styles.benefitCard}>
            <div className={styles.iconWrapper}>
              <i className="fas fa-shield-alt"></i>
            </div>
            <h3>Confident Decisions</h3>
            <p>
              Make informed choices with our AI recommendations based on
              data-driven analysis
            </p>
          </div>
        </div>
      </section> */}

      {/* How It Works Section */}
      <section className={styles.howItWorksSection}>
        <div className={styles.container}>
          <div className={styles.sectionTitle}>
            <h2>How Trakvu Works</h2>
            <p>From upload to decision in minutes. Stop wondering, start knowing.</p>
          </div>

          {/* Desktop version - show all steps */}
          <div className={styles.processSteps}>
            <div className={styles.processStep}>
              <div className={styles.stepNumberWrapper}>
                <div className={styles.stepNumber}>1</div>
                <div className={styles.stepConnector}></div>
              </div>
              <div className={styles.stepContent}>
                <div className={styles.stepIcon}>📝</div>
                <h3>Enter Project Details & Upload</h3>
                <p>Tell us about your project and upload your contractor quotes. Takes just 2 minutes.</p>
                <div className={styles.stepFeatures}>
                  <span className={styles.stepFeature}>Project description</span>
                  <span className={styles.stepFeature}>Upload quotes (PDF/images)</span>
                  <span className={styles.stepFeature}>Set your budget</span>
                </div>
              </div>
            </div>
            <div className={styles.processStep}>
              <div className={styles.stepNumberWrapper}>
                <div className={styles.stepNumber}>2</div>
                <div className={styles.stepConnector}></div>
              </div>
              <div className={styles.stepContent}>
                <div className={styles.stepIcon}>🤖</div>
                <h3>AI Analysis Magic</h3>
                <p>Our AI analyzes every detail, finds gaps, flags risks, and compares pricing in real-time.</p>
                <div className={styles.stepFeatures}>
                  <span className={styles.stepFeature}>Missing items detection</span>
                  <span className={styles.stepFeature}>Price comparison</span>
                  <span className={styles.stepFeature}>Quality assessment</span>
                </div>
              </div>
            </div>
            <div className={styles.processStep}>
              <div className={styles.stepNumberWrapper}>
                <div className={styles.stepNumber}>3</div>
              </div>
              <div className={styles.stepContent}>
                <div className={styles.stepIcon}>📊</div>
                <h3>Review & Decide</h3>
                <p>Get your personalized report with clear recommendations and make the best choice with confidence.</p>
                <div className={styles.stepFeatures}>
                  <span className={styles.stepFeature}>Detailed comparison</span>
                  <span className={styles.stepFeature}>Risk warnings</span>
                  <span className={styles.stepFeature}>Best value recommendation</span>
                </div>
              </div>
            </div>
          </div>

          {/* Mobile version - step switcher */}
          <div className={styles.mobileStepSwitcher}>
            {/* Step buttons */}
            <div className={styles.stepButtons} key={renderKey}>
              {[1, 2, 3].map((stepNumber) => (
                <button
                  key={`${renderKey}-${stepNumber}`}
                  className={`${styles.stepButton} ${activeStep === stepNumber ? styles.active : ''}`}
                  onClick={() => handleStepClick(stepNumber)}
                >
                  {stepNumber}
                </button>
              ))}
            </div>

            {/* Step content */}
            <div className={styles.mobileStepContent}>
              {activeStep === 1 && (
                <div className={styles.mobileStep}>
                  <div className={styles.stepIcon}>📝</div>
                  <h3>Enter Project Details & Upload</h3>
                  <p>Tell us about your project and upload your contractor quotes. Takes just 2 minutes.</p>
                  <div className={styles.stepFeatures}>
                    <span className={styles.stepFeature}>Project description</span>
                    <span className={styles.stepFeature}>Upload quotes (PDF/images)</span>
                    <span className={styles.stepFeature}>Set your budget</span>
                  </div>
                </div>
              )}
              
              {activeStep === 2 && (
                <div className={styles.mobileStep}>
                  <div className={styles.stepIcon}>🤖</div>
                  <h3>AI Analysis Magic</h3>
                  <p>Our AI analyzes every detail, finds gaps, flags risks, and compares pricing in real-time.</p>
                  <div className={styles.stepFeatures}>
                    <span className={styles.stepFeature}>Missing items detection</span>
                    <span className={styles.stepFeature}>Price comparison</span>
                    <span className={styles.stepFeature}>Quality assessment</span>
                  </div>
                </div>
              )}

              {activeStep === 3 && (
                <div className={styles.mobileStep}>
                  <div className={styles.stepIcon}>📊</div>
                  <h3>Review & Decide</h3>
                  <p>Get your personalized report with clear recommendations and make the best choice with confidence.</p>
                  <div className={styles.stepFeatures}>
                    <span className={styles.stepFeature}>Detailed comparison</span>
                    <span className={styles.stepFeature}>Risk warnings</span>
                    <span className={styles.stepFeature}>Best value recommendation</span>
                  </div>
                </div>
              )}
            </div>

            {/* Progress indicator */}
            <div className={styles.stepProgress}>
              <div className={styles.progressBar}>
                <div 
                  className={styles.progressFill}
                  style={{ width: `${(activeStep / 3) * 100}%` }}
                ></div>
              </div>
              <span className={styles.progressText}>Step {activeStep} of 3</span>
            </div>
          </div>
        </div>
      </section>

      {/* Popular Projects Section */}
      {/* <section className={styles.projectsSection}>
        <div className={styles.sectionTitle}>
          <h2>Renovation Projects We Can Help With</h2>
          <p>
            Let our AI analyze contractor proposals for these popular home
            improvements
          </p>
        </div>
        <div className={styles.projectsGrid}>
          <div className={styles.projectCard}>
            <div className={styles.projectIcon}>
              <i className="fas fa-kitchen-set"></i>
            </div>
            <h3>Kitchen Remodeling</h3>
            <p>
              Compare contractor proposals for your dream kitchen renovation
            </p>
            <Link
              to="/homeowner/create-comparison?type=kitchen"
              className={styles.projectLink}
            >
              Start Project <i className="fas fa-arrow-right"></i>
            </Link>
          </div>
          <div className={styles.projectCard}>
            <div className={styles.projectIcon}>
              <i className="fas fa-bath"></i>
            </div>
            <h3>Bathroom Renovation</h3>
            <p>
              Find the best contractor for your bathroom makeover with AI
              assistance
            </p>
            <Link
              to="/homeowner/create-comparison?type=bathroom"
              className={styles.projectLink}
            >
              Start Project <i className="fas fa-arrow-right"></i>
            </Link>
          </div>
          <div className={styles.projectCard}>
            <div className={styles.projectIcon}>
              <i className="fas fa-home"></i>
            </div>
            <h3>Home Addition</h3>
            <p>
              Analyze proposals for adding new space to your existing home
            </p>
            <Link
              to="/homeowner/create-comparison?type=addition"
              className={styles.projectLink}
            >
              Start Project <i className="fas fa-arrow-right"></i>
            </Link>
          </div>
          <div className={styles.projectCard}>
            <div className={styles.projectIcon}>
              <i className="fas fa-solar-panel"></i>
            </div>
            <h3>Energy Efficiency</h3>
            <p>
              Compare contractor proposals for energy-saving home improvements
            </p>
            <Link
              to="/homeowner/create-comparison?type=energy"
              className={styles.projectLink}
            >
              Start Project <i className="fas fa-arrow-right"></i>
            </Link>
          </div>
        </div>
      </section> */}

      {/* Video Section */}
      <section className={styles.videoSection}>
        <div className={styles.sectionTitle}>
          <h2>See SmartTech ProServe in Action</h2>
          <p>
            Watch how our AI-powered platform helps homeowners make smarter renovation decisions
          </p>
        </div>
        <div className={styles.videoContainer}>
          <div className={styles.videoWrapper}>
            <video 
              className={styles.video}
              controls
              preload="metadata"
              poster="/assets/video-poster.jpg"
            >
              <source src="/assets/smarttech-demo.mp4" type="video/mp4" />
              <source src="/assets/smarttech-demo.webm" type="video/webm" />
              Your browser does not support the video tag.
            </video>
          </div>
          <div className={styles.videoInfo}>
            <h3>How It Works: From Project Submission to AI Recommendation</h3>
            <p>
              This 3-minute demo shows you exactly how to submit your renovation project, 
              add contractor proposals, and get AI-powered recommendations to choose the best option.
            </p>
            <div className={styles.videoFeatures}>
              <div className={styles.feature}>
                <i className="fas fa-clock"></i>
                <span>3 min demo</span>
              </div>
              <div className={styles.feature}>
                <i className="fas fa-eye"></i>
                <span>Real project walkthrough</span>
              </div>
              <div className={styles.feature}>
                <i className="fas fa-brain"></i>
                <span>AI analysis explained</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      {/* <section className={styles.testimonialsSection}>
        <div className={styles.sectionTitle}>
          <h2>What Homeowners Are Saying</h2>
          <p>
            Real stories from homeowners who used our AI to find the best
            contractor
          </p>
        </div>
        <div className={styles.testimonialGrid}>
          <div className={styles.testimonialCard}>
            <div className={styles.testimonialQuote}>
              "SmartTech ProServe's AI analysis saved me from choosing the wrong
              contractor. It compared five different proposals and highlighted
              issues I would have missed. The contractor it recommended
              delivered exceptional work on my kitchen remodel."
            </div>
            <div className={styles.testimonialAuthor}>
              <div className={styles.testimonialAvatar}></div>
              <div>
                <h4>Jennifer K.</h4>
                <p>Kitchen Remodeling Project</p>
              </div>
            </div>
          </div>
          <div className={styles.testimonialCard}>
            <div className={styles.testimonialQuote}>
              "I was overwhelmed by four different bathroom renovation quotes
              until I used SmartTech ProServe. Their AI analyzed each proposal
              in detail and showed me which one offered the best value. The
              recommended contractor completed the work exactly as promised!"
            </div>
            <div className={styles.testimonialAuthor}>
              <div className={styles.testimonialAvatar}></div>
              <div>
                <h4>Michael T.</h4>
                <p>Bathroom Renovation Project</p>
              </div>
            </div>
          </div>
        </div>
      </section> */}

      {/* CTA Section */}
      {/* <section className={styles.cta}>
        <div className={styles.ctaContent}>
          <h2>Ready to make the best decision for your renovation?</h2>
          <p>
            Let our AI analyze contractor proposals and recommend the best
            option
          </p>
          <Link to="/homeowner/create-comparison" className={styles.ctaButton}>
            Submit Your Project
            <i className="fas fa-chevron-right"></i>
          </Link>
        </div>
      </section> */}

      {/* Site Footer with Disclaimer */}
      <footer className={styles.siteFooter}>
        <div className={styles.container}>
          <div className={styles.disclaimerContent}>
            <p>
              Trakvu.com is a tool to help homeowners compare contractor bids. We do not verify the accuracy, legality, or completeness of any contractor quote.
            </p>
            <p>
              All decisions are your own. Trakvu.com, SmartTech ProServe, and their owners, employees, or vendors are not responsible for any damages, disputes, or project outcomes related to your use of this site.
            </p>
          </div>
          <div className={styles.footerLinks}>
            <span>Used by real homeowners to catch fake promises and inflated scopes—before it's too late.</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomeOwner; 